import '../stylesheets/auth_wizard.css';

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Alert, Button, Col, Form, Row } from 'react-bootstrap';
import {
  GoogleReCaptchaProvider, // eslint-disable-line @typescript-eslint/no-unused-vars -- Provider in App.js; import satisfies jsx-no-undef
  useGoogleReCaptcha,
} from 'react-google-recaptcha-v3';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';

import {
  checkAuthEmailStatus,
  clearAuthSession,
  finalizeAuth,
  getMyAuthorizedAccounts,
  getOauthProfile,
  initiateAuth,
  isAuthSessionForCurrentEnvironment,
  loginRequest,
  oauthExchangeRequest,
  persistAuthSession,
  requestAuthEmailCode,
  saveAuthBoat,
  saveAuthPayment,
  saveAuthProfile,
  sendAuthMobileCode,
  submitAuthPassword,
  verifyAuthEmailCode,
  verifyAuthMobileCode,
} from '../actions/auth';
import characterImg from '../assets/auth/character3.png';
import eyeClosedIcon from '../assets/auth/eye-closed.svg';
import eyeOpenIcon from '../assets/auth/eye-open.svg';
import logoSvg from '../assets/logo.svg';
import { COMMUNICATION_LANGUAGES, CURRENCIES } from '../constants/constants';
import { getApiErrorMessage as resolveApiErrorMessage } from '../utils/apiErrors';
import { isMobileMode, postToNative } from '../utils/mobileBridge';
import {
  errorsExist,
  validateBoatForm,
  validateProfileForm,
} from '../utils/validators';
import { showAlert } from './AlertModal';
import { authModalRef } from './AuthModal';
import AuthPhoneInput from './AuthPhoneInput';
import AuthSocialButtons from './AuthSocialButtons';
import FormCheckbox from './FormCheckbox';
import FormInput from './FormInput';
import VerificationCodeInput from './VerificationCodeInput';

const STEP_EMAIL = 'email';
const STEP_LOGIN = 'login';
const STEP_REGISTER_CHOICE = 'register_choice';
const STEP_REGISTER_PASSWORD = 'register_password';
const STEP_EMAIL_CONFIRM = 'email_confirm';
const STEP_PROFILE = 'profile';
const STEP_MOBILE_CONFIRM = 'mobile_confirm';
const STEP_BOAT = 'boat';
const STEP_PAYMENT = 'payment';
const STEP_COMPLETION = 'completion';

const AUTH_EMAIL_COOKIE = 'auth_email';
const AUTH_WIZARD_UUID_KEY = 'auth_wizard_uuid';
const AUTH_WIZARD_STEP_KEY = 'auth_wizard_step';
const AUTH_PROFILE_KEY = 'auth_profile';
const AUTH_BOAT_KEY = 'auth_boat';
const AUTH_COUNTRY_KEY = 'auth_country';
const AUTH_FLOW_KEY = 'auth_flow';
/** OAuth sign-in on the email step; empty for this release. Re-enable: `['google']`. */
const AUTH_OAUTH_PROVIDERS = [];
const CAPTCHA_LANGUAGE_VARIABLES = new Set([
  'captchaFailed',
  'recaptchaFailed',
  'recaptchaNotLoaded',
]);
const AUTH_UI_LANGUAGE_KEY = 'auth_ui_language';
const AUTH_COMPLETION_FLOW_COOKIE = 'auth_completion_login_intent';
const COMPLETION_FLOW_COOKIE_DURATION_DAYS = 15 / (24 * 60);
const AUTH_WIZARD_STEPS = [
  STEP_EMAIL,
  STEP_LOGIN,
  STEP_REGISTER_CHOICE,
  STEP_REGISTER_PASSWORD,
  STEP_EMAIL_CONFIRM,
  STEP_PROFILE,
  STEP_MOBILE_CONFIRM,
  STEP_BOAT,
  STEP_PAYMENT,
  STEP_COMPLETION,
];

function getLocalStorage() {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.localStorage;
  } catch (error) {
    return null;
  }
}

function readPersistedValue(key) {
  const storage = getLocalStorage();
  if (!storage) {
    return null;
  }
  try {
    return storage.getItem(key);
  } catch (error) {
    return null;
  }
}

function writePersistedValue(key, value) {
  const storage = getLocalStorage();
  if (!storage) {
    return;
  }
  try {
    storage.setItem(key, value);
  } catch (error) {
    // ignore write failures
  }
}

function deletePersistedValue(key) {
  const storage = getLocalStorage();
  if (!storage) {
    return;
  }
  try {
    storage.removeItem(key);
  } catch (error) {
    // ignore removal failures
  }
}

function readPersistedJson(key) {
  const raw = readPersistedValue(key);
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw);
  } catch (error) {
    return null;
  }
}

function writePersistedJson(key, value) {
  if (value === undefined) {
    deletePersistedValue(key);
    return;
  }
  try {
    writePersistedValue(key, JSON.stringify(value));
  } catch (error) {
    // ignore write failures
  }
}

function readCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop().split(';').shift();
  }
  return '';
}

function writeCookie(name, value, days) {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
}

function deleteCookie(name) {
  if (typeof document === 'undefined') {
    return;
  }
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
}

function isValidEmail(email) {
  if (!email) {
    return false;
  }
  const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return pattern.test(String(email).toLowerCase());
}

function generateRandomPassword(length = 24) {
  const charset =
    'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*';
  if (typeof window !== 'undefined' && window.crypto?.getRandomValues) {
    const bytes = new Uint32Array(length);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (value) => charset[value % charset.length]).join(
      '',
    );
  }
  return Array.from({ length }, () =>
    charset[Math.floor(Math.random() * charset.length)],
  ).join('');
}

const ROLE_OPTIONS = ['Private', 'Marina'];
const CAMPER_WOMO_INDEX = 4;
const BOOKING_FLOW = 'booking';
const GUEST_ROLE = 'Guest';

function CharacterGuide({ hint }) {
  const getViewportWidth = () => {
    if (typeof window === 'undefined') {
      return 1200;
    }
    return window.innerWidth;
  };
  const [displayedHint, setDisplayedHint] = useState('');
  const [viewportWidth, setViewportWidth] = useState(() => getViewportWidth());
  const isMobile = viewportWidth < 992;
  const [bubbleHeight, setBubbleHeight] = useState(null);
  const measureRef = useRef(null);

  useEffect(() => {
    setDisplayedHint('');
    if (!hint) {
      return undefined;
    }
    let index = 0;
    const timer = setInterval(() => {
      index += 1;
      setDisplayedHint(hint.slice(0, index));
      if (index >= hint.length) {
        clearInterval(timer);
      }
    }, 25);
    return () => clearInterval(timer);
  }, [hint]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useLayoutEffect(() => {
    if (!isMobile) {
      setBubbleHeight(null);
      return;
    }
    const node = measureRef.current;
    if (node) {
      const nextHeight = node.scrollHeight;
      setBubbleHeight(nextHeight > 0 ? nextHeight : null);
    }
  }, [hint, isMobile, viewportWidth]);

  const typing = hint ? displayedHint.length !== hint.length : false;
  const textStyle =
    isMobile && bubbleHeight ? { minHeight: `${bubbleHeight}px` } : undefined;

  return (
    <div className='auth-character'>
      <div className='auth-character__visual'>
        <img src={characterImg} alt='' />
        <div className='auth-character__placeholder'></div>
      </div>
      <div className='auth-chat-bubble'>
        <div className='auth-chat-bubble__content'>
          <div className='auth-chat-bubble__text' style={textStyle}>
            {displayedHint}
          </div>
          <div
            aria-hidden='true'
            ref={measureRef}
            className='auth-chat-bubble__text auth-chat-bubble__text-measure'>
            {hint}
          </div>
          <div
            className={`auth-chat-bubble__typing${typing ? '' : ' is-hidden'}`}>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
        <div className='auth-chat-bubble__mobile-image'>
          <img src={characterImg} alt='' />
        </div>
      </div>
    </div>
  );
}

class Auth extends React.Component {
  constructor(props) {
    super(props);
    const defaultLanguage = this.getDefaultLanguage();
    this.state = {
      currentStep: STEP_EMAIL,
      flow: null,
      loading: false,
      errorMessage: '',
      successMessage: '',
      oauthProvider: null,
      apiContext: {},
      email: '',
      loginPassword: '',
      loginPasswordVisible: false,
      registerPassword: '',
      registerConfirmPassword: '',
      registerConfirmVisible: false,
      registerPasswordsVisible: false,
      emailCode: '',
      emailConfirmationAddress: '',
      emailConfirmed: false,
      boatAdded: false,
      paymentAdded: false,
      returnToCompletionAfterBoat: false,
      profile: {
        role: 'Private',
        firstName: '',
        lastName: '',
        language: defaultLanguage,
        phone: '',
        acceptTerms: false,
      },
      mobileCode: '',
      mobileConfirmed: false,
      smsCountdown: 0,
      hasTriggeredSms: false,
      boatDetails: {
        type: null,
        name: '',
        registration: '',
        length: '',
        beam: '',
        draft: '',
      },
      paymentDetails: {
        currency: '',
      },
      registerErrors: {},
      profileErrors: {},
      codeErrors: {},
      boatErrors: {},
      paymentErrors: {},
      phoneCountry: undefined,
      uiLanguage: defaultLanguage,
      activationRequirement: null,
      stepLoading: false,
      captchaToken: '',
    };

    this.smsInterval = null;
    this.emailCheckInterval = null;
    this.paymentStatusInterval = null;
    this.defaultLanguage = defaultLanguage;
    this.firstNameInputRef = null;
    this.executeRecaptcha = null;
  }

  componentDidMount() {
    if (this.redirectIfAlreadyAuthenticated()) {
      return;
    }

    if (!readPersistedValue(AUTH_FLOW_KEY)) {
      writePersistedValue(AUTH_FLOW_KEY, this.props.flow || 'email');
    }
    const cookieEmail = readCookie(AUTH_EMAIL_COOKIE);
    const { prefillEmail } = this.props;
    this.setState((prevState) => {
      const nextState = {
        profile: {
          ...prevState.profile,
          language: this.defaultLanguage,
        },
      };
      // Prefill email from mooring transfer URL param, or fall back to cookie
      if (prefillEmail) {
        nextState.email = prefillEmail;
        nextState.emailConfirmationAddress = prefillEmail;
      } else if (cookieEmail) {
        nextState.email = cookieEmail;
        nextState.emailConfirmationAddress = cookieEmail;
      }
      return nextState;
    });

    this.restorePersistedProgress();
    this.restoreSavedForms();
    this.restoreSelectedCountry();
    this.handlePaymentStatusFromUrl();
    this.handleOauthExchangeFromUrl();
    this.handleOauthProfileFromUrl();

    if (this.state.smsCountdown > 0) {
      this.startSmsInterval();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.smsCountdown > 0 && prevState.smsCountdown <= 0) {
      this.startSmsInterval();
    } else if (this.state.smsCountdown <= 0 && prevState.smsCountdown > 0) {
      this.clearSmsInterval();
    }

    if (
      this.state.currentStep === STEP_MOBILE_CONFIRM &&
      !this.state.hasTriggeredSms &&
      (prevState.currentStep !== this.state.currentStep ||
        prevState.hasTriggeredSms !== this.state.hasTriggeredSms)
    ) {
      // this.handleSendSms();
    }

    const currentUuid = this.state.apiContext?.uuid;
    const previousUuid = prevState.apiContext?.uuid;

    if (
      this.state.currentStep === STEP_COMPLETION &&
      currentUuid &&
      (prevState.currentStep !== this.state.currentStep ||
        previousUuid !== currentUuid)
    ) {
      this.refreshCompletionStatus();
    }

    if (
      this.state.currentStep !== prevState.currentStep &&
      this.isValidStep(this.state.currentStep)
    ) {
      this.persistCurrentStep(this.state.currentStep);
      this.scrollToTopOnMobile();
    }

    if (this.state.flow !== prevState.flow) {
      if (this.state.flow) {
        writePersistedValue(AUTH_FLOW_KEY, this.state.flow);
      } else {
        deletePersistedValue(AUTH_FLOW_KEY);
      }
    }

    if (
      this.state.currentStep === STEP_PROFILE &&
      prevState.currentStep !== STEP_PROFILE
    ) {
      this.focusFirstNameInput();
    }

    if (
      this.state.currentStep === STEP_COMPLETION &&
      prevState.currentStep !== STEP_COMPLETION
    ) {
      this.setCompletionFlowCookie();
    }

    if (currentUuid && currentUuid !== previousUuid) {
      this.persistUuid(currentUuid);
    }

    if (this.state.currentStep === STEP_EMAIL_CONFIRM) {
      const hasTransitioned = prevState.currentStep !== this.state.currentStep;
      const uuidUpdated = currentUuid && currentUuid !== previousUuid;
      const emailUpdated =
        prevState.emailConfirmationAddress !==
        this.state.emailConfirmationAddress;
      if (hasTransitioned || uuidUpdated || emailUpdated) {
        this.startEmailCheckInterval();
      }
    } else if (prevState.currentStep === STEP_EMAIL_CONFIRM) {
      this.clearEmailCheckInterval();
    }

    if (!prevState.paymentAdded && this.state.paymentAdded) {
      this.clearPaymentStatusInterval();
    } else if (
      prevState.currentStep === STEP_COMPLETION &&
      this.state.currentStep !== STEP_COMPLETION
    ) {
      this.clearPaymentStatusInterval();
    }
  }

  componentWillUnmount() {
    this.clearSmsInterval();
    this.clearEmailCheckInterval();
    this.clearPaymentStatusInterval();
  }

  setFirstNameInputRef = (node) => {
    this.firstNameInputRef = node;
  };

  setExecuteRecaptcha = (executeRecaptcha) => {
    this.executeRecaptcha = executeRecaptcha;
  };

  getCaptchaToken = async (action) => {
    if (!this.executeRecaptcha) {
      return '';
    }
    try {
      return (await this.executeRecaptcha(action)) || '';
    } catch (error) {
      console.error('reCAPTCHA error:', error);
      return '';
    }
  };

  getCaptchaFailureMessage = () => {
    const { t } = this.props;
    const translated = t('errors.captchaFailed');
    if (
      typeof translated === 'string' &&
      translated.trim() &&
      translated !== 'errors.captchaFailed'
    ) {
      return translated;
    }
    return t('auth.errorGeneric');
  };

  isCaptchaError = (error) => {
    const languageVariable = String(
      error?.response?.data?.language_variable || '',
    ).toLowerCase();
    return (
      CAPTCHA_LANGUAGE_VARIABLES.has(languageVariable) ||
      languageVariable.includes('captcha')
    );
  };

  resolveCaptchaToken = async (action) => {
    const token = await this.getCaptchaToken(action);
    if (token) {
      return { token };
    }
    return { errorMessage: this.getCaptchaFailureMessage() };
  };

  focusFirstNameInput = () => {
    if (isMobileMode()) {
      return;
    }
    if (
      this.firstNameInputRef &&
      typeof this.firstNameInputRef.focus === 'function'
    ) {
      this.firstNameInputRef.focus();
    }
  };

  setCompletionFlowCookie = () => {
    writeCookie(
      AUTH_COMPLETION_FLOW_COOKIE,
      '1',
      COMPLETION_FLOW_COOKIE_DURATION_DAYS,
    );
  };

  clearCompletionFlowCookie = () => {
    deleteCookie(AUTH_COMPLETION_FLOW_COOKIE);
  };

  redirectIfAlreadyAuthenticated = () => {
    const storage = getLocalStorage();
    if (!storage) {
      return false;
    }
    try {
      if (!isAuthSessionForCurrentEnvironment()) {
        clearAuthSession();
        return false;
      }
      const token = storage.getItem('AuthToken');
      if (!token) {
        return false;
      }
      const completionFlag = readCookie(AUTH_COMPLETION_FLOW_COOKIE);
      if (completionFlag) {
        this.setState({ currentStep: STEP_COMPLETION });
        return false;
      }
      const storedRole = storage.getItem('role') || '';
      this.redirectAfterLogin(storedRole);
      return true;
    } catch (error) {
      return false;
    }
  };

  restorePersistedProgress = () => {
    const persistedUuid = readPersistedValue(AUTH_WIZARD_UUID_KEY);
    const persistedStep = readPersistedValue(AUTH_WIZARD_STEP_KEY);
    const persistedFlow = readPersistedValue(AUTH_FLOW_KEY);

    if (!persistedUuid && !persistedStep && !persistedFlow) {
      return;
    }

    this.setState((prevState) => {
      const nextState = {};
      if (persistedUuid && persistedUuid !== prevState.apiContext?.uuid) {
        nextState.apiContext = {
          ...prevState.apiContext,
          uuid: persistedUuid,
        };
      }

      if (
        persistedStep &&
        this.isValidStep(persistedStep) &&
        persistedStep !== prevState.currentStep
      ) {
        nextState.currentStep = persistedStep;
      }
      if (persistedFlow && persistedFlow !== prevState.flow) {
        nextState.flow = persistedFlow;
      }

      return Object.keys(nextState).length ? nextState : null;
    });
  };

  restoreSavedForms = () => {
    const savedProfile = readPersistedJson(AUTH_PROFILE_KEY);
    const savedBoat = readPersistedJson(AUTH_BOAT_KEY);
    if (!savedProfile && !savedBoat) {
      return;
    }
    this.setState((prevState) => {
      const nextState = {};
      if (savedProfile && typeof savedProfile === 'object') {
        nextState.profile = {
          ...prevState.profile,
          ...savedProfile,
        };
      }
      if (savedBoat && typeof savedBoat === 'object') {
        nextState.boatDetails = {
          ...prevState.boatDetails,
          ...savedBoat,
        };
      }
      return Object.keys(nextState).length ? nextState : null;
    });
  };

  restoreSelectedCountry = () => {
    const persistedCountry = readPersistedValue(AUTH_COUNTRY_KEY);
    if (!persistedCountry) {
      return;
    }
    this.setState({ phoneCountry: persistedCountry });
  };

  handlePaymentStatusFromUrl = () => {
    if (typeof window === 'undefined') {
      return;
    }
    const { history, location, t } = this.props;
    const search =
      (location && location.search) || window.location.search || '';
    if (!search || !search.includes('ps=')) {
      return;
    }
    let params;
    try {
      params = new URLSearchParams(search);
    } catch (error) {
      return;
    }
    const rawStatus = params.get('ps') || '';
    const rawUuid = params.get('uuid') || '';
    const status = rawStatus.trim().toLowerCase();
    const uuid = rawUuid.trim();
    if (!status || !uuid || !['s', 'f', 'c'].includes(status)) {
      return;
    }

    // Preserve mobile=1 parameter if it exists in the URL or was previously active
    const mobileParam = params.get('mobile');
    const wasMobileMode = mobileParam === '1' || isMobileMode();
    const cleanPath = wasMobileMode ? '/auth?mobile=1' : '/auth';
    if (history && typeof history.replace === 'function') {
      history.replace(cleanPath);
    } else if (
      window.history &&
      typeof window.history.replaceState === 'function'
    ) {
      window.history.replaceState(null, '', cleanPath);
    }

    const persistedUuid = readPersistedValue(AUTH_WIZARD_UUID_KEY);
    if (!persistedUuid || persistedUuid !== uuid) {
      return;
    }

    if (status === 's') {
      this.setState({
        currentStep: STEP_COMPLETION,
        errorMessage: '',
      });
      return;
    }

    const errorKey =
      status === 'c' ? 'auth.paymentCancelled' : 'auth.paymentFailed';
    this.setState({
      currentStep: STEP_PAYMENT,
      errorMessage: t(errorKey),
      returnToCompletionAfterBoat: false,
    });
  };

  handleOauthProfileFromUrl = () => {
    if (typeof window === 'undefined') {
      return false;
    }
    const { history, location } = this.props;
    const search =
      (location && location.search) || window.location.search || '';
    if (!search || !search.includes('oauth=1') || !search.includes('uuid=')) {
      return false;
    }
    let params;
    try {
      params = new URLSearchParams(search);
    } catch (error) {
      return false;
    }
    const oauthFlag = (params.get('oauth') || '').trim();
    const uuid = (params.get('uuid') || '').trim();
    if (oauthFlag !== '1' || !uuid) {
      return false;
    }

    writePersistedValue(AUTH_FLOW_KEY, 'oauth');
    const mobileParam = params.get('mobile');
    const wasMobileMode = mobileParam === '1' || isMobileMode();
    const cleanPath = wasMobileMode ? '/auth?mobile=1' : '/auth';
    if (history && typeof history.replace === 'function') {
      history.replace(cleanPath);
    } else if (
      window.history &&
      typeof window.history.replaceState === 'function'
    ) {
      window.history.replaceState(null, '', cleanPath);
    }

    this.setState({ loading: true, stepLoading: true, errorMessage: '' });
    getOauthProfile({ uuid })
      .then((response) => {
        const data = response?.data || {};
        this.setState((prevState) => ({
          loading: false,
          stepLoading: false,
          apiContext: {
            ...prevState.apiContext,
            ...data,
            uuid: data.uuid || uuid,
          },
          email: data.email || prevState.email,
          profile: {
            ...prevState.profile,
            firstName: data.first_name || prevState.profile.firstName,
            lastName: data.last_name || prevState.profile.lastName,
          },
          currentStep: STEP_PROFILE,
        }));
      })
      .catch((error) => {
        this.setState({
          loading: false,
          stepLoading: false,
          errorMessage: this.getApiErrorMessage(error),
        });
      });
    return true;
  };

  handleOauthExchangeFromUrl = () => {
    if (typeof window === 'undefined') {
      return false;
    }
    const { history, location } = this.props;
    const search =
      (location && location.search) || window.location.search || '';
    if (!search || !search.includes('oauth_token')) {
      return false;
    }
    let params;
    try {
      params = new URLSearchParams(search);
    } catch (error) {
      return false;
    }
    const oauthToken = (params.get('oauth_token') || '').trim();
    if (!oauthToken) {
      return false;
    }

    const mobileParam = params.get('mobile');
    const wasMobileMode = mobileParam === '1' || isMobileMode();
    const cleanPath = wasMobileMode ? '/auth?mobile=1' : '/auth';
    if (history && typeof history.replace === 'function') {
      history.replace(cleanPath);
    } else if (
      window.history &&
      typeof window.history.replaceState === 'function'
    ) {
      window.history.replaceState(null, '', cleanPath);
    }

    return this.handleOauthExchange(oauthToken);
  };

  handleOauthExchange = (oauthToken, silent = false) => {
    if (!oauthToken) {
      return false;
    }
    const storage = getLocalStorage();
    const finalizeLogin = (authorizedAccounts = [], loginResponse) => {
      if (storage) {
        storage.setItem(
          'AuthorizedAccounts',
          JSON.stringify(authorizedAccounts),
        );
      }
      const roleSource =
        loginResponse?.account?.role ||
        loginResponse?.role ||
        storage?.getItem('role') ||
        '';
      this.redirectAfterLogin((roleSource || '').toLowerCase(), loginResponse);
    };

    if (!silent) {
      this.clearCompletionFlowCookie();
      this.setState({ loading: true, stepLoading: true, errorMessage: '' });
    } else {
      this.setState({ errorMessage: '' });
    }
    oauthExchangeRequest(
      oauthToken,
      (response) => {
        this.setState((prevState) => ({
          loginResponse: response,
          apiContext: {
            ...prevState.apiContext,
            ...response,
          },
          ...(silent ? {} : { loading: false, stepLoading: false }),
        }));
        if (!silent) {
          getMyAuthorizedAccounts(
            (authorized_accounts) => {
              finalizeLogin(authorized_accounts, response);
            },
            () => {
              finalizeLogin([], response);
            },
          );
        }
      },
      (error) => {
        this.setState({
          errorMessage: this.getApiErrorMessage(error),
          ...(silent ? {} : { loading: false, stepLoading: false }),
        });
      },
    );
    return true;
  };

  setPersistedCountry = (country) => {
    const normalizedCountry =
      typeof country === 'string' ? country.trim().toUpperCase() : '';
    const nextCountry = normalizedCountry || undefined;
    this.setState((prevState) => {
      if (prevState.phoneCountry === nextCountry) {
        return null;
      }
      return { phoneCountry: nextCountry };
    });
    if (normalizedCountry) {
      writePersistedValue(AUTH_COUNTRY_KEY, normalizedCountry);
    } else {
      deletePersistedValue(AUTH_COUNTRY_KEY);
    }
  };

  isValidStep = (step) => {
    return AUTH_WIZARD_STEPS.includes(step);
  };

  persistCurrentStep = (step) => {
    if (!this.isValidStep(step)) {
      deletePersistedValue(AUTH_WIZARD_STEP_KEY);
      return;
    }
    writePersistedValue(AUTH_WIZARD_STEP_KEY, step);
  };

  scrollToTopOnMobile = () => {
    if (typeof window === 'undefined') {
      return;
    }
    if (window.innerWidth < 992) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  persistUuid = (uuid) => {
    if (uuid) {
      writePersistedValue(AUTH_WIZARD_UUID_KEY, uuid);
    }
  };

  clearPersistedProgress = () => {
    deletePersistedValue(AUTH_WIZARD_STEP_KEY);
    deletePersistedValue(AUTH_WIZARD_UUID_KEY);
    deletePersistedValue(AUTH_PROFILE_KEY);
    deletePersistedValue(AUTH_BOAT_KEY);
    deletePersistedValue(AUTH_COUNTRY_KEY);
    deletePersistedValue(AUTH_FLOW_KEY);
  };

  startEmailCheckInterval = () => {
    const { emailConfirmationAddress, apiContext } = this.state;
    if (
      this.emailCheckInterval ||
      !emailConfirmationAddress ||
      !apiContext?.uuid
    ) {
      return;
    }
    this.emailCheckInterval = setInterval(
      this.pollEmailConfirmationStatus,
      3000,
    );
  };

  clearEmailCheckInterval = () => {
    if (this.emailCheckInterval) {
      clearInterval(this.emailCheckInterval);
      this.emailCheckInterval = null;
    }
  };

  pollEmailConfirmationStatus = async () => {
    const { emailConfirmationAddress, apiContext, currentStep } = this.state;
    if (
      currentStep !== STEP_EMAIL_CONFIRM ||
      !emailConfirmationAddress ||
      !apiContext?.uuid
    ) {
      return;
    }

    try {
      const response = await checkAuthEmailStatus({
        email: emailConfirmationAddress,
        uuid: apiContext.uuid,
      });
      if (response?.data?.status === 'confirmed') {
        this.clearEmailCheckInterval();
        this.setState({
          codeErrors: {},
          emailConfirmed: true,
          currentStep: STEP_PROFILE,
        });
      }
    } catch (error) {
      // ignore polling errors
    }
  };

  getDefaultLanguage() {
    const persisted = readPersistedValue(AUTH_UI_LANGUAGE_KEY);
    if (persisted && COMMUNICATION_LANGUAGES.includes(persisted)) {
      return persisted;
    }
    const i18nLanguage = this.props?.i18n?.language;
    if (i18nLanguage) {
      const normalized = i18nLanguage.split('-')[0].toUpperCase();
      if (COMMUNICATION_LANGUAGES.includes(normalized)) {
        return normalized;
      }
    }
    const sourceLanguage =
      typeof navigator !== 'undefined' && navigator.language
        ? navigator.language
        : 'en';
    const browserLanguage = sourceLanguage.split('-')[0].toUpperCase();
    if (COMMUNICATION_LANGUAGES.includes(browserLanguage)) {
      return browserLanguage;
    }
    return 'EN';
  }

  getBoatTypes = () => {
    const { t } = this.props;
    const types = t('global.boatTypes', { returnObjects: true });
    return Array.isArray(types) ? types : [];
  };

  getOauthUrls = () => {
    const apiHost = process.env.REACT_APP_API_HOST || '';
    return {
      // google: `${apiHost}auth/google`, // re-enable with AUTH_OAUTH_PROVIDERS + Google button
      facebook: `${apiHost}auth/facebook`,
      apple: `${apiHost}auth/apple`,
    };
  };

  resetAlerts = () => {
    this.setState({
      errorMessage: '',
      successMessage: '',
    });
  };

  startSmsInterval = () => {
    this.clearSmsInterval();
    this.smsInterval = setInterval(() => {
      this.setState((prevState) => {
        const nextValue =
          prevState.smsCountdown > 0 ? prevState.smsCountdown - 1 : 0;
        if (nextValue === 0) {
          this.clearSmsInterval();
        }
        return {
          smsCountdown: nextValue,
        };
      });
    }, 1000);
  };

  clearSmsInterval = () => {
    if (this.smsInterval) {
      clearInterval(this.smsInterval);
      this.smsInterval = null;
    }
  };

  startPaymentStatusInterval = () => {
    if (!this.state.apiContext?.uuid) {
      return;
    }
    this.clearPaymentStatusInterval();
    this.paymentStatusInterval = setInterval(
      this.refreshCompletionStatus,
      3000,
    );
  };

  clearPaymentStatusInterval = () => {
    if (this.paymentStatusInterval) {
      clearInterval(this.paymentStatusInterval);
      this.paymentStatusInterval = null;
    }
  };

  getApiErrorMessage = (error, fallbackKey = 'auth.errorGeneric') => {
    const { t, i18n } = this.props;
    const resolved = resolveApiErrorMessage(error, t);
    const somethingWentWrong = t('errors.somethingWentWrong');
    if (
      resolved &&
      resolved !== somethingWentWrong &&
      resolved !== 'Something went wrong, please try again.'
    ) {
      return resolved;
    }
    const languageVariable = error?.response?.data?.language_variable;
    if (languageVariable) {
      const candidateKeys = [
        languageVariable,
        `errors.${languageVariable}`,
        `auth.${languageVariable}`,
      ];
      const hasTranslation = (key) => {
        if (!key) {
          return false;
        }
        if (typeof i18n?.exists === 'function') {
          return i18n.exists(key);
        }
        const translation = t(key);
        return translation && translation !== key;
      };
      const matchingKey = candidateKeys.find((key) => hasTranslation(key));
      if (matchingKey) {
        return t(matchingKey);
      }
    }
    return t(fallbackKey);
  };

  mapProviderName = () => {
    const { t } = this.props;
    const { oauthProvider } = this.state;
    if (!oauthProvider) {
      return '';
    }
    if (oauthProvider === 'google') {
      return t('auth.providerGoogle');
    }
    if (oauthProvider === 'apple') {
      return t('auth.providerApple');
    }
    if (oauthProvider === 'facebook') {
      return t('auth.providerFacebook');
    }
    return oauthProvider;
  };

  redirectToProvider = (provider) => {
    const oauthUrls = this.getOauthUrls();
    const url = oauthUrls[provider] || '';
    if (!url) {
      console.info('Configure OAuth redirect URL for provider', provider);
      return;
    }
    window.location.href = url;
  };

  handleSendSms = async () => {
    const { t } = this.props;
    const { profile, apiContext } = this.state;
    if (!profile.phone) {
      this.setState((prevState) => ({
        profileErrors: {
          ...prevState.profileErrors,
          phone: t('required'),
        },
      }));
      return;
    }
    this.resetAlerts();
    this.setState({ loading: true });
    try {
      const captcha = await this.resolveCaptchaToken('mobile_send');
      if (captcha.errorMessage) {
        this.setState({ errorMessage: captcha.errorMessage });
        return;
      }

      await sendAuthMobileCode({
        phone: profile.phone,
        uuid: apiContext.uuid,
        captchaToken: captcha.token,
      });
      this.setState({
        hasTriggeredSms: true,
        smsCountdown: 60,
        successMessage: t('auth.smsSent'),
      });
    } catch (error) {
      this.setState({ errorMessage: this.getApiErrorMessage(error) });
    } finally {
      this.setState({ loading: false });
    }
  };

  handlePaymentSkip = () => {
    this.resetAlerts();
    this.setState({
      paymentErrors: {},
      currentStep: STEP_COMPLETION,
      returnToCompletionAfterBoat: false,
    });
  };

  refreshCompletionStatus = async () => {
    const { apiContext } = this.state;
    try {
      const response = await finalizeAuth({
        uuid: apiContext.uuid,
      });
      this.setState((prevState) => ({
        emailConfirmed: response.data?.emailConfirmed
          ? true
          : prevState.emailConfirmed,
        mobileConfirmed: response.data?.mobileConfirmed
          ? true
          : prevState.mobileConfirmed,
        boatAdded: response.data?.boatAdded ? true : prevState.boatAdded,
        paymentAdded: response.data?.paymentAdded
          ? true
          : prevState.paymentAdded,
      }));
    } catch (error) {
      // optional step, ignore errors
    }
  };

  handleEmailSubmit = async () => {
    const { t, i18n } = this.props;
    const { email } = this.state;

    this.resetAlerts();
    this.setState({ registerErrors: {} });

    if (!isValidEmail(email)) {
      this.setState({ registerErrors: { email: t('invalidEmail') } });
      return;
    }

    this.setState({ loading: true });
    try {
      const response = await initiateAuth({
        email,
        language: i18n.language,
      });
      const data = response.data || {};
      const result = data.action;
      this.setPersistedCountry(data.country);
      writeCookie(AUTH_EMAIL_COOKIE, email, 30);
      this.setState((prevState) => {
        const updatedState = {
          apiContext: data,
          oauthProvider: data.provider || null,
          emailConfirmationAddress: email,
          registerConfirmVisible: prevState.registerConfirmVisible,
          flow: null,
        };

        if (result === 'login') {
          updatedState.flow = 'login';
          updatedState.currentStep = STEP_LOGIN;
        } else if (result === 'register') {
          if (this.isBookingFlowEnabled()) {
            updatedState.flow = BOOKING_FLOW;
            updatedState.currentStep = STEP_REGISTER_CHOICE;
          } else {
            updatedState.flow = 'register';
            updatedState.currentStep = STEP_REGISTER_PASSWORD;
          }
          updatedState.registerConfirmVisible = false;
        } else if (result === 'oauth') {
          updatedState.flow = 'oauth';
        }

        return updatedState;
      });
    } catch (error) {
      this.setState({ errorMessage: this.getApiErrorMessage(error) });
    } finally {
      this.setState({ loading: false });
    }
  };

  handleLoginSubmit = async (silent = false) => {
    const { t } = this.props;
    const { email, loginPassword, registerPassword } = this.state;
    const storage = getLocalStorage();

    if (!silent) {
      this.clearCompletionFlowCookie();
    }
    const finalizeLogin = (authorizedAccounts = [], loginResponse) => {
      if (storage) {
        storage.setItem(
          'AuthorizedAccounts',
          JSON.stringify(authorizedAccounts),
        );
      }
      const roleSource =
        loginResponse?.account?.role ||
        loginResponse?.role ||
        storage?.getItem('role') ||
        '';

      if (!silent) {
        this.redirectAfterLogin(
          (roleSource || '').toLowerCase(),
          loginResponse,
        );
      }
    };

    if (!silent && storage?.getItem('AuthorizedAccounts')) {
      getMyAuthorizedAccounts(
        (authorized_accounts) => {
          finalizeLogin(authorized_accounts);
        },
        () => {
          finalizeLogin([]);
        },
      );
      return;
    }

    this.resetAlerts();
    const passwordToUse = silent ? registerPassword : loginPassword;
    if (!passwordToUse) {
      if (!silent) {
        const authFlow = readPersistedValue(AUTH_FLOW_KEY);
        if (authFlow === 'oauth') {
          const storage = getLocalStorage();
          const roleSource =
            storage?.getItem('role') || this.state.loginResponse?.role || '';
          this.redirectAfterLogin(
            (roleSource || '').toLowerCase(),
            this.state.loginResponse,
          );
          return;
        }
        this.setState((prevState) => ({
          registerErrors: { password: t('required') },
          currentStep:
            prevState.currentStep === STEP_REGISTER_PASSWORD
              ? prevState.currentStep
              : STEP_EMAIL,
        }));
      }
      return;
    }

    if (!silent) {
      this.setState({ loading: true });
    }

    // Check for Firebase device token cookie from mobile app
    const deviceToken = readCookie('firebase_device_token');
    const isMobileWebflow = !!deviceToken;
    const captcha = await this.resolveCaptchaToken('login');
    if (captcha.errorMessage) {
      if (!silent) {
        this.setState({
          loading: false,
          registerErrors: {},
          errorMessage: captcha.errorMessage,
        });
      }
      return;
    }

    loginRequest(
      {
        email,
        password: passwordToUse,
        platform: isMobileWebflow ? 'mobile-webflow' : 'React',
        app_version: '0',
        captchaToken: captcha.token,
        ...(deviceToken && { device_token: deviceToken }),
      },
      (response) => {
        // Notify mobile app when login succeeds
        this.setState({ loginResponse: response });
        this.setState((prevState) => {
          const nextState = {
            apiContext: {
              ...prevState.apiContext,
              ...response,
            },
          };
          if (!silent) {
            nextState.loading = false;
          }
          return nextState;
        });

        getMyAuthorizedAccounts(
          (authorized_accounts) => {
            finalizeLogin(authorized_accounts, response);
          },
          () => {
            finalizeLogin([], response);
          },
        );
      },
      (error) => {
        const languageVariable = error?.response?.data?.language_variable || '';
        if (languageVariable === 'pleaseActivateMobile') {
          const uuid = error?.response?.data?.uuid;
          const mobileNumber = error?.response?.data?.mobilenumber;
          this.setState(
            (prevState) => {
              const nextState = {
                registerErrors: {},
                apiContext: {
                  ...prevState.apiContext,
                  uuid: uuid || prevState.apiContext?.uuid,
                },
                profile: {
                  ...prevState.profile,
                  phone: mobileNumber || prevState.profile.phone,
                },
                currentStep: STEP_MOBILE_CONFIRM,
                hasTriggeredSms: false,
                smsCountdown: 0,
                mobileCode: '',
                activationRequirement: 'mobile',
              };
              if (!silent) {
                nextState.loading = false;
              }
              return nextState;
            },
            () => {
              if (this.state.profile.phone) {
                this.handleSendSms();
              }
            },
          );
          return;
        }
        if (languageVariable === 'pleaseActivateEmail') {
          const uuid = error?.response?.data?.uuid;
          this.setState(
            (prevState) => {
              const nextState = {
                registerErrors: {},
                apiContext: {
                  ...prevState.apiContext,
                  uuid: uuid || prevState.apiContext?.uuid,
                },
                emailConfirmationAddress: prevState.email,
                emailCode: '',
                currentStep: STEP_EMAIL_CONFIRM,
                activationRequirement: 'email',
              };
              if (!silent) {
                nextState.loading = false;
              }
              return nextState;
            },
            () => {
              this.handleResendEmail();
            },
          );
          return;
        }
        if (!silent) {
          if (this.isCaptchaError(error)) {
            this.setState({
              loading: false,
              registerErrors: {},
              errorMessage: this.getApiErrorMessage(
                error,
                'errors.captchaFailed',
              ),
            });
            return;
          }
          this.setState({
            loading: false,
            registerErrors: { password: t('invalidCredentials') },
          });
        }
      },
    );
  };

  redirectAfterLogin = (role, loginResponse = null) => {
    const { history, onAuthSuccess } = this.props;

    if (isMobileMode()) {
      // Use the passed loginResponse or fall back to state
      const response = loginResponse || this.state.loginResponse;
      const storage = getLocalStorage();
      const token = response?.token || storage?.getItem('AuthToken');
      const responseData = response || (token ? { token } : {});
      postToNative('LOGIN_SUCCESS', {
        response: responseData,
        token: token,
      });
      postToNative('CLOSE');
      return;
    }
    const normalizedRole = (role || '').toLowerCase();
    this.clearPersistedProgress();
    if (typeof onAuthSuccess === 'function') {
      onAuthSuccess();
    }
    if (this.isBookingFlowEnabled()) {
      return;
    }
    if (normalizedRole === 'marina') {
      history.push(`/home`);
      if (typeof window !== 'undefined') {
        window.location.reload();
      }
    } else {
      history.push(`/FindMoorings`);
    }
  };

  handleRegisterSubmit = async () => {
    const { t } = this.props;
    const {
      registerPassword,
      registerConfirmPassword,
      registerConfirmVisible,
      apiContext,
      email,
      uiLanguage,
    } = this.state;

    this.resetAlerts();
    const errors = {};

    if (!registerPassword || registerPassword.length < 6) {
      errors.password = t('passwordTooShort');
    }
    if (registerConfirmVisible) {
      if (!registerConfirmPassword) {
        errors.confirmPassword = t('required');
      } else if (registerPassword !== registerConfirmPassword) {
        errors.confirmPassword = t('repeatedPasswordWrong');
      }
    }

    if (Object.keys(errors).length) {
      this.setState({ registerErrors: errors });
      return;
    }

    if (!registerConfirmVisible) {
      this.setState({
        registerErrors: {},
        registerConfirmVisible: true,
      });
      return;
    }

    this.setState({ loading: true });

    try {
      const captcha = await this.resolveCaptchaToken('password');
      if (captcha.errorMessage) {
        this.setState({
          registerErrors: {},
          errorMessage: captcha.errorMessage,
        });
        return;
      }

      const response = await submitAuthPassword({
        email,
        password: registerPassword,
        token: apiContext.token,
        lang: uiLanguage,
        captchaToken: captcha.token,
      });
      this.setState((prevState) => ({
        apiContext: {
          ...prevState.apiContext,
          ...response.data,
        },
        registerErrors: {},
        currentStep: STEP_EMAIL_CONFIRM,
      }));
    } catch (error) {
      if (this.isCaptchaError(error)) {
        this.setState({
          registerErrors: {},
          errorMessage: this.getApiErrorMessage(error, 'errors.captchaFailed'),
        });
        return;
      }
      this.setState({
        registerErrors: { password: this.getApiErrorMessage(error) },
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  handleVerifyEmail = async () => {
    const { t } = this.props;
    const {
      emailConfirmationAddress,
      emailCode,
      apiContext,
      activationRequirement,
    } = this.state;

    this.resetAlerts();

    if (!emailConfirmationAddress || !isValidEmail(emailConfirmationAddress)) {
      this.setState({
        codeErrors: { email: t('invalidEmail') },
      });
      return;
    }
    if (!emailCode || emailCode.length !== 6) {
      this.setState({
        codeErrors: { code: t('required') },
      });
      return;
    }

    this.setState({ loading: true });

    try {
      await verifyAuthEmailCode({
        email: emailConfirmationAddress,
        code: emailCode,
        uuid: apiContext.uuid,
      });
      this.clearEmailCheckInterval();
      const forcedEmailActivation = activationRequirement === 'email';
      this.setState({
        codeErrors: {},
        emailConfirmed: true,
        currentStep: forcedEmailActivation ? STEP_COMPLETION : STEP_PROFILE,
      });
    } catch (error) {
      this.setState({
        codeErrors: { code: t('validators.codeInvalid') },
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  handleResendEmail = async () => {
    const { t } = this.props;
    const { emailConfirmationAddress, apiContext } = this.state;

    this.resetAlerts();

    if (!emailConfirmationAddress || !isValidEmail(emailConfirmationAddress)) {
      this.setState({
        codeErrors: { email: t('.invalidEmail') },
      });
      return;
    }

    this.setState({ loading: true });

    try {
      const captcha = await this.resolveCaptchaToken('email_resend');
      if (captcha.errorMessage) {
        this.setState({ errorMessage: captcha.errorMessage });
        return;
      }

      await requestAuthEmailCode({
        email: emailConfirmationAddress,
        uuid: apiContext.uuid,
        captchaToken: captcha.token,
      });
      this.setState({
        successMessage: t('auth.emailResent'),
      });
      writeCookie(AUTH_EMAIL_COOKIE, emailConfirmationAddress, 30);
    } catch (error) {
      this.setState({
        errorMessage: this.getApiErrorMessage(error),
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  handleProfileSubmit = async () => {
    const { mooringTransferToken } = this.props;
    const { profile, apiContext } = this.state;

    this.resetAlerts();

    const errors = validateProfileForm(profile);

    if (Object.values(errors).some((error) => error !== false)) {
      console.log('profile errors', errors);
      this.setState({ profileErrors: errors });
      return;
    }

    this.setState({ loading: true });

    try {
      const captcha = await this.resolveCaptchaToken('profile');
      if (captcha.errorMessage) {
        this.setState({ errorMessage: captcha.errorMessage });
        return;
      }

      const payload = {
        uuid: apiContext.uuid,
        role: this.isBookingGuestFlow() ? GUEST_ROLE : profile.role,
        firstName: profile.firstName,
        lastName: profile.lastName,
        language: profile.language,
        phone: profile.phone,
        captchaToken: captcha.token,
      };
      if (mooringTransferToken) {
        payload.mooringTransferToken = mooringTransferToken;
      }
      const response = await saveAuthProfile(payload);
      this.setState((prevState) => ({
        profileErrors: {},
        apiContext: {
          ...prevState.apiContext,
          ...response.data,
        },
        hasTriggeredSms: false,
        smsCountdown: 0,
        mobileCode: '',
        currentStep: STEP_MOBILE_CONFIRM,
      }));
      writePersistedJson(AUTH_PROFILE_KEY, profile);
    } catch (error) {
      this.setState({ errorMessage: this.getApiErrorMessage(error) });
    } finally {
      this.setState({ loading: false });
    }
  };

  handleVerifyMobile = async () => {
    const { t } = this.props;
    const { mobileCode, apiContext, profile, activationRequirement } =
      this.state;

    this.resetAlerts();

    if (!mobileCode || mobileCode.length !== 5) {
      this.setState({
        codeErrors: { mobile: t('required') },
      });
      return;
    }

    this.setState({ loading: true });

    try {
      const response = await verifyAuthMobileCode({
        code: mobileCode,
        uuid: apiContext.uuid,
        phone: profile.phone,
      });
      const oauthToken = response?.data?.oauth_token;
      if (oauthToken) {
        this.handleOauthExchange(oauthToken, true);
      }
      const forcedMobileActivation = activationRequirement === 'mobile';
      this.setState(
        {
          codeErrors: {},
          mobileConfirmed: true,
          hasTriggeredSms: false,
          smsCountdown: 0,
          mobileCode: '',
          currentStep: forcedMobileActivation ? STEP_COMPLETION : STEP_BOAT,
          returnToCompletionAfterBoat: false,
        },
        () => {
          if (!forcedMobileActivation && !this.isBookingGuestFlow()) {
            this.handleLoginSubmit(true);
          }
        },
      );
    } catch (error) {
      this.setState({
        codeErrors: { mobile: t('validators.codeInvalid') },
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  handleBoatSubmit = async () => {
    const { t } = this.props;
    const { boatDetails, apiContext } = this.state;

    this.resetAlerts();

    const errors = validateBoatForm(boatDetails);

    if (errorsExist(errors)) {
      this.setState({ boatErrors: errors });
      console.log('boat errors', errors);
      return;
    }

    this.setState({ loading: true });

    try {
      const response = await saveAuthBoat({
        uuid: apiContext.uuid,
        ...boatDetails,
      });
      if (this.isBookingGuestFlow()) {
        this.completeBookingGuestLogin(response?.data);
        return;
      }
      this.setState((prevState) => ({
        boatErrors: {},
        currentStep: prevState.returnToCompletionAfterBoat
          ? STEP_COMPLETION
          : STEP_PAYMENT,
        returnToCompletionAfterBoat: false,
      }));
      writePersistedJson(AUTH_BOAT_KEY, boatDetails);
    } catch (error) {
      this.setState({ errorMessage: t('auth.errorGeneric') });
    } finally {
      this.setState({ loading: false });
    }
  };

  handleBoatSkip = () => {
    this.resetAlerts();
    this.setState((prevState) => ({
      boatErrors: {},
      currentStep: prevState.returnToCompletionAfterBoat
        ? STEP_COMPLETION
        : STEP_PAYMENT,
      returnToCompletionAfterBoat: false,
    }));
  };

  handlePaymentSubmit = async () => {
    const { t } = this.props;
    const { paymentDetails, apiContext } = this.state;

    this.resetAlerts();

    const errors = {};
    if (!paymentDetails.currency) {
      errors.currency = t('required');
    }
    if (Object.keys(errors).length) {
      this.setState({ paymentErrors: errors });
      return;
    }

    this.setState({ loading: true });

    try {
      const response = await saveAuthPayment({
        uuid: apiContext.uuid,
        ...paymentDetails,
      });
      console.log('response', response);
      const redirectLink = response?.data?.link;
      if (!redirectLink) {
        this.setState({
          paymentErrors: {},
          errorMessage: t('auth.paymentLinkMissing', {
            defaultValue: t('auth.errorGeneric'),
          }),
        });
        return;
      }
      // console.log('redirectLink', redirectLink);
      window.location.assign(redirectLink);

      this.setState(
        {
          currentStep: STEP_COMPLETION,
          loading: false,
          returnToCompletionAfterBoat: false,
        },
        () => {
          this.startPaymentStatusInterval();
        },
      );
    } catch (error) {
      this.setState({ errorMessage: t('auth.errorGeneric') });
    } finally {
      this.setState({ loading: false });
    }
  };

  handleBack = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // In mobile mode, if we're at the first step, send close message to native app
    if (isMobileMode() && this.state.currentStep === STEP_EMAIL) {
      postToNative('CLOSE');
      return;
    }

    this.resetAlerts();
    this.setState((prevState) => {
      if (
        prevState.activationRequirement === 'email' &&
        prevState.currentStep === STEP_EMAIL_CONFIRM
      ) {
        return {
          currentStep: STEP_EMAIL,
          activationRequirement: null,
        };
      }
      if (
        prevState.activationRequirement === 'mobile' &&
        prevState.currentStep === STEP_MOBILE_CONFIRM
      ) {
        return {
          currentStep: STEP_EMAIL,
          activationRequirement: null,
          hasTriggeredSms: false,
          smsCountdown: 0,
          mobileCode: '',
        };
      }
      if (
        prevState.activationRequirement &&
        prevState.currentStep === STEP_COMPLETION
      ) {
        return {
          currentStep: STEP_EMAIL,
          activationRequirement: null,
        };
      }
      if (
        prevState.currentStep === STEP_LOGIN ||
        prevState.currentStep === STEP_REGISTER_CHOICE ||
        prevState.currentStep === STEP_REGISTER_PASSWORD
      ) {
        if (
          prevState.currentStep === STEP_REGISTER_PASSWORD &&
          prevState.registerConfirmVisible
        ) {
          return { registerConfirmVisible: false };
        }
        if (
          prevState.currentStep === STEP_REGISTER_PASSWORD &&
          this.isBookingFlowEnabled()
        ) {
          return {
            currentStep: STEP_REGISTER_CHOICE,
            flow: BOOKING_FLOW,
          };
        }
        return { currentStep: STEP_EMAIL };
      }
      if (prevState.currentStep === STEP_EMAIL_CONFIRM) {
        return { currentStep: STEP_REGISTER_PASSWORD };
      }
      if (prevState.currentStep === STEP_PROFILE) {
        return {
          currentStep: this.isBookingGuestFlow()
            ? STEP_REGISTER_CHOICE
            : STEP_EMAIL_CONFIRM,
        };
      }
      if (prevState.currentStep === STEP_MOBILE_CONFIRM) {
        return {
          hasTriggeredSms: false,
          smsCountdown: 0,
          currentStep: STEP_PROFILE,
        };
      }
      if (prevState.currentStep === STEP_BOAT) {
        if (this.isBookingGuestFlow()) {
          return {
            currentStep: STEP_MOBILE_CONFIRM,
            returnToCompletionAfterBoat: false,
          };
        }
        return {
          currentStep: STEP_COMPLETION,
          returnToCompletionAfterBoat: false,
        };
      }
      if (prevState.currentStep === STEP_PAYMENT) {
        return {
          currentStep: STEP_COMPLETION,
          returnToCompletionAfterBoat: false,
        };
      }
      return {};
    });
  };

  handleRoleChange = (role) => {
    const { t } = this.props;
    this.setState((prevState) => ({
      profile: {
        ...prevState.profile,
        role,
      },
    }));
    if (role === 'Marina') {
      showAlert(
        t('auth.marinaModalTitle'),
        t('auth.marinaModalBody'),
      );
    }
  };

  handlePhoneCountryChange = (country) => {
    this.setPersistedCountry(country);
  };

  handleUiLanguageChange = (event) => {
    const nextLanguage = event.target.value;
    const { i18n } = this.props;
    if (!COMMUNICATION_LANGUAGES.includes(nextLanguage)) {
      return;
    }
    this.setState((prevState) => ({
      uiLanguage: nextLanguage,
      profile: {
        ...prevState.profile,
        language: nextLanguage,
      },
    }));
    if (i18n?.changeLanguage) {
      i18n.changeLanguage(nextLanguage.toLowerCase());
    }
    writePersistedValue(AUTH_UI_LANGUAGE_KEY, nextLanguage);
  };

  isBookingFlowEnabled = () => {
    return this.props.flow === BOOKING_FLOW;
  };

  isBookingGuestFlow = () => {
    return this.state.flow === BOOKING_FLOW;
  };

  handleChooseRegisterAccount = () => {
    this.setState({
      flow: 'register',
      registerConfirmVisible: false,
      registerErrors: {},
      currentStep: STEP_REGISTER_PASSWORD,
    });
  };

  handleChooseGuestBooking = async () => {
    const { email, apiContext, uiLanguage } = this.state;
    const guestPassword = generateRandomPassword();

    this.resetAlerts();
    this.setState({ loading: true, registerErrors: {} });

    try {
      const captcha = await this.resolveCaptchaToken('guest_password');
      if (captcha.errorMessage) {
        this.setState({ errorMessage: captcha.errorMessage });
        return;
      }
      const response = await submitAuthPassword({
        email,
        password: guestPassword,
        token: apiContext.token,
        lang: uiLanguage,
        captchaToken: captcha.token,
        flow: BOOKING_FLOW,
      });

      this.setState((prevState) => ({
        flow: BOOKING_FLOW,
        registerPassword: guestPassword,
        registerConfirmVisible: false,
        registerErrors: {},
        emailConfirmed: true,
        currentStep: STEP_PROFILE,
        apiContext: {
          ...prevState.apiContext,
          ...response.data,
        },
        profile: {
          ...prevState.profile,
          role: GUEST_ROLE,
        },
      }));
    } catch (error) {
      this.setState({
        errorMessage: this.getApiErrorMessage(error),
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  completeBookingGuestLogin = (responseData = {}) => {
    const { t } = this.props;
    if (!responseData?.token || !responseData?.account || !responseData?.role) {
      this.setState({
        errorMessage: t('auth.errorGeneric'),
      });
      return;
    }

    persistAuthSession(responseData);
    getMyAuthorizedAccounts(
      (authorized_accounts) => {
        const storage = getLocalStorage();
        if (storage) {
          storage.setItem(
            'AuthorizedAccounts',
            JSON.stringify(authorized_accounts),
          );
        }
        this.redirectAfterLogin(
          (responseData.role || '').toLowerCase(),
          responseData,
        );
      },
      () => {
        this.redirectAfterLogin(
          (responseData.role || '').toLowerCase(),
          responseData,
        );
      },
    );
  };

  getHintText = () => {
    const { t } = this.props;
    const { currentStep, flow, registerConfirmVisible } = this.state;
    const providerName = this.mapProviderName();

    if (currentStep === STEP_EMAIL) {
      if (flow === 'oauth' && providerName) {
        return t('auth.hintOauth').replace('{{provider}}', providerName);
      }
      return t('auth.hintWelcome');
    }
    if (currentStep === STEP_LOGIN) {
      return t('auth.hintLogin');
    }
    if (currentStep === STEP_REGISTER_CHOICE) {
      return t('auth.hintRegisterChoice', {
        defaultValue:
          'Choose whether you want the full BoatPark account experience or a faster one-time guest booking flow.',
      });
    }
    if (currentStep === STEP_REGISTER_PASSWORD) {
      return registerConfirmVisible
        ? t('auth.hintRegisterConfirm')
        : t('auth.hintRegisterPassword');
    }
    if (currentStep === STEP_EMAIL_CONFIRM) {
      return t('auth.hintConfirmEmail');
    }
    if (currentStep === STEP_PROFILE) {
      return this.isBookingGuestFlow()
        ? t('auth.hintBookingGuestProfile', {
            defaultValue:
              'Share the details needed for this booking. We will set up your guest access without email verification or a password.',
          })
        : t('auth.hintProfile');
    }
    if (currentStep === STEP_MOBILE_CONFIRM) {
      return t('auth.hintConfirmMobile');
    }
    if (currentStep === STEP_BOAT) {
      return t('auth.hintBoat');
    }
    if (currentStep === STEP_PAYMENT) {
      return t('auth.hintPayment');
    }
    return t('auth.hintCompletion');
  };

  getStepTitle = () => {
    const { t } = this.props;
    const { currentStep } = this.state;

    if (currentStep === STEP_EMAIL) {
      return t('auth.stepEmailTitle');
    }
    if (currentStep === STEP_LOGIN) {
      return t('auth.stepLoginTitle');
    }
    if (currentStep === STEP_REGISTER_CHOICE) {
      return t('auth.stepRegisterChoiceTitle', {
        defaultValue: 'Choose your booking path',
      });
    }
    if (currentStep === STEP_REGISTER_PASSWORD) {
      return t('auth.stepRegisterTitle');
    }
    if (currentStep === STEP_EMAIL_CONFIRM) {
      return t('auth.stepEmailConfirmTitle');
    }
    if (currentStep === STEP_PROFILE) {
      return t('auth.stepProfileTitle');
    }
    if (currentStep === STEP_MOBILE_CONFIRM) {
      return t('auth.stepMobileConfirmTitle');
    }
    if (currentStep === STEP_BOAT) {
      return t('auth.stepBoatTitle');
    }
    if (currentStep === STEP_PAYMENT) {
      return t('auth.stepPaymentTitle');
    }
    return t('auth.stepCompletionTitle');
  };

  getOauthInstruction = () => {
    const { t } = this.props;
    const providerName = this.mapProviderName();
    if (!providerName) {
      return t('auth.oauthContinueMessage');
    }
    return t('auth.oauthContinueWithProvider').replace(
      '{{provider}}',
      providerName,
    );
  };

  renderEmailStep = () => {
    const { t } = this.props;
    const { email, registerErrors, flow, loading, oauthProvider } = this.state;
    const providerName = this.mapProviderName();
    const oauthInstruction = this.getOauthInstruction();

    return (
      <Form
        onSubmit={(event) => {
          event.preventDefault();
          this.handleEmailSubmit();
        }}>
        <FormInput
          id='auth_email'
          name='username'
          autoComplete='username'
          label={t('auth.emailLabel')}
          type='email'
          value={email}
          error={registerErrors.email}
          onChange={(event) => {
            const value = event.target.value;
            this.setState((prevState) => ({
              email: value,
              registerErrors: {
                ...prevState.registerErrors,
                email: undefined,
              },
            }));
          }}
          placeholder={t('auth.emailPlaceholder')}
          autoFocus
        />
        {flow === 'oauth' && (
          <Alert variant='info' className='auth-inline-alert'>
            {providerName
              ? t('auth.oauthInfoWithProvider').replace(
                  '{{provider}}',
                  providerName,
                )
              : t('auth.oauthInfo')}
          </Alert>
        )}
        {AUTH_OAUTH_PROVIDERS.length > 0 && (
          <div className='auth-social-wrapper'>
            {flow === 'oauth' && (
              <div className='auth-social-message'>{oauthInstruction}</div>
            )}
            <AuthSocialButtons
              providers={AUTH_OAUTH_PROVIDERS}
              onProviderClick={this.redirectToProvider}
              disabled={loading}
              highlightedProvider={oauthProvider}
            />
          </div>
        )}
        <div className='auth-actions'>
          <div>
            <Button type='submit' disabled={loading}>
              {t('auth.next')}
            </Button>
          </div>
        </div>
      </Form>
    );
  };

  renderRegisterChoiceStep = () => {
    const { t } = this.props;
    const { loading } = this.state;

    return (
      <div className='auth-choice-step'>
        <div className='auth-choice-grid'>
          <button
            type='button'
            className='auth-choice-card'
            onClick={this.handleChooseRegisterAccount}
            disabled={loading}>
            <span className='auth-choice-card__eyebrow'>
              {t('auth.registerChoiceAccountEyebrow', {
                defaultValue: 'Best for regular trips',
              })}
            </span>
            <span className='auth-choice-card__title'>
              {t('auth.registerChoiceAccountTitle', {
                defaultValue: 'Create an account',
              })}
            </span>
            <span className='auth-choice-card__description'>
              {t('auth.registerChoiceAccountDescription', {
                defaultValue:
                  'Save your details once, keep your boats and payment setup ready, and make future bookings much faster.',
              })}
            </span>
          </button>
          <button
            type='button'
            className='auth-choice-card auth-choice-card--secondary'
            onClick={this.handleChooseGuestBooking}
            disabled={loading}>
            <span className='auth-choice-card__eyebrow'>
              {t('auth.registerChoiceGuestEyebrow', {
                defaultValue: 'Fastest for this reservation',
              })}
            </span>
            <span className='auth-choice-card__title'>
              {t('auth.registerChoiceGuestTitle', {
                defaultValue: 'Book as a guest',
              })}
            </span>
            <span className='auth-choice-card__description'>
              {t('auth.registerChoiceGuestDescription', {
                defaultValue:
                  'Continue without a password or email confirmation and provide only the details needed to finish this booking.',
              })}
            </span>
          </button>
        </div>
        <div className='auth-actions'>
          <div>
            <Button
              type='button'
              variant='link'
              onClick={this.handleBack}
              disabled={loading}>
              {t('auth.back')}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  renderLoginStep = () => {
    const { t } = this.props;
    const {
      email,
      loginPassword,
      loginPasswordVisible,
      registerErrors,
      loading,
    } = this.state;

    return (
      <Form
        onSubmit={(event) => {
          event.preventDefault();
          this.handleLoginSubmit();
        }}>
        <FormInput
          id='auth_login_email'
          name='username'
          autoComplete='username'
          label={t('auth.emailLabel')}
          type='email'
          value={email}
          readOnly
        />
        <div className='auth-password-field'>
          <FormInput
            id='auth_login_password'
            name='password'
            autoComplete='current-password'
            label={t('auth.passwordLabel')}
            type={loginPasswordVisible ? 'text' : 'password'}
            value={loginPassword}
            error={registerErrors.password}
            onChange={(event) => {
              const value = event.target.value;
              this.setState((prevState) => ({
                loginPassword: value,
                registerErrors: {
                  ...prevState.registerErrors,
                  password: undefined,
                },
              }));
            }}
            autoFocus
          />
          <button
            type='button'
            className='auth-password-toggle'
            onClick={() =>
              this.setState((prevState) => ({
                loginPasswordVisible: !prevState.loginPasswordVisible,
              }))
            }>
            <img
              src={loginPasswordVisible ? eyeClosedIcon : eyeOpenIcon}
              alt={t('auth.togglePassword')}
            />
          </button>
        </div>
        <div className='auth-password-links'>
          <Link
            to={`/ForgotPassword/${email}`}
            onClick={() => {
              authModalRef.current?.hide();
            }}>
            {t('auth.forgotPassword')}
          </Link>
        </div>
        <div className='auth-actions'>
          <div>
            <Button
              type='button'
              variant='link'
              onClick={this.handleBack}
              disabled={loading}>
              {t('auth.back')}
            </Button>
            <Button type='submit' disabled={loading}>
              {t('auth.next')}
            </Button>
          </div>
        </div>
      </Form>
    );
  };

  renderRegisterStep = () => {
    const { t } = this.props;
    const {
      email,
      registerPassword,
      registerConfirmPassword,
      registerConfirmVisible,
      registerPasswordsVisible,
      registerErrors,
      loading,
    } = this.state;

    return (
      <Form
        onSubmit={(event) => {
          event.preventDefault();
          this.handleRegisterSubmit();
        }}>
        <FormInput
          id='auth_register_email'
          name='username'
          autoComplete='username'
          label={t('auth.emailLabel')}
          type='email'
          value={email}
          readOnly
        />
        <div className='auth-password-field'>
          <FormInput
            id='auth_register_password'
            name='new-password'
            autoComplete='new-password'
            label={t('auth.passwordLabel')}
            type={registerPasswordsVisible ? 'text' : 'password'}
            value={registerPassword}
            error={registerErrors.password}
            onChange={(event) => {
              const value = event.target.value;
              this.setState((prevState) => ({
                registerPassword: value,
                registerErrors: {
                  ...prevState.registerErrors,
                  password: undefined,
                },
              }));
            }}
            autoFocus={!registerConfirmVisible && !isMobileMode()}
          />
          <button
            type='button'
            className='auth-password-toggle'
            onClick={() =>
              this.setState((prevState) => ({
                registerPasswordsVisible: !prevState.registerPasswordsVisible,
              }))
            }>
            <img
              src={registerPasswordsVisible ? eyeClosedIcon : eyeOpenIcon}
              alt={t('auth.togglePassword')}
            />
          </button>
        </div>
        {registerConfirmVisible && (
          <div className='auth-password-field'>
            <FormInput
              id='auth_register_confirm_password'
              name='confirm-password'
              autoComplete='new-password'
              label={t('auth.confirmPasswordLabel')}
              type={registerPasswordsVisible ? 'text' : 'password'}
              value={registerConfirmPassword}
              error={registerErrors.confirmPassword}
              onChange={(event) => {
                const value = event.target.value;
                this.setState((prevState) => ({
                  registerConfirmPassword: value,
                  registerErrors: {
                    ...prevState.registerErrors,
                    confirmPassword: undefined,
                  },
                }));
              }}
              autoFocus={registerConfirmVisible && !isMobileMode()}
            />
            <button
              type='button'
              className='auth-password-toggle'
              onClick={() =>
                this.setState((prevState) => ({
                  registerPasswordsVisible: !prevState.registerPasswordsVisible,
                }))
              }>
              <img
                src={registerPasswordsVisible ? eyeClosedIcon : eyeOpenIcon}
                alt={t('auth.togglePassword')}
              />
            </button>
          </div>
        )}
        <div className='auth-actions'>
          <div>
            <Button
              type='button'
              variant='link'
              onClick={this.handleBack}
              disabled={loading}>
              {t('auth.back')}
            </Button>
            <Button type='submit' disabled={loading}>
              {t('auth.next')}
            </Button>
          </div>
        </div>
      </Form>
    );
  };

  renderEmailConfirmStep = () => {
    const { t } = this.props;
    const { emailConfirmationAddress, emailCode, codeErrors, loading } =
      this.state;

    return (
      <Form
        onSubmit={(event) => {
          event.preventDefault();
          this.handleVerifyEmail();
        }}>
        <FormInput
          id='auth_confirm_email'
          label={t('auth.emailLabel')}
          type='email'
          value={emailConfirmationAddress}
          error={codeErrors.email}
          onChange={(event) => {
            const value = event.target.value;
            this.setState((prevState) => ({
              emailConfirmationAddress: value,
              codeErrors: {
                ...prevState.codeErrors,
                email: undefined,
              },
            }));
          }}
        />
        <VerificationCodeInput
          value={emailCode}
          onChange={(value) => {
            this.setState((prevState) => ({
              emailCode: value,
              codeErrors: {
                ...prevState.codeErrors,
                code: undefined,
              },
            }));
          }}
          idPrefix='auth_email_code'
          label={t('auth.verificationCodeLabel')}
          autoFocus={!isMobileMode()}
        />
        {codeErrors.code && (
          <div className='auth-error-inline'>{codeErrors.code}</div>
        )}
        <div className='auth-inline-actions'>
          <Button
            type='button'
            variant='link'
            className='auth-resend-link'
            onClick={this.handleResendEmail}
            disabled={loading}>
            {t('auth.resendEmail')}
          </Button>
        </div>
        <div className='auth-actions'>
          <div>
            <Button
              type='button'
              variant='link'
              onClick={this.handleBack}
              disabled={loading}>
              {t('auth.back')}
            </Button>
            <Button type='submit' disabled={loading}>
              {t('auth.next')}
            </Button>
          </div>
        </div>
      </Form>
    );
  };

  renderProfileStep = () => {
    const { t } = this.props;
    const { profile, profileErrors, loading, phoneCountry } = this.state;
    const defaultPhoneCountry = phoneCountry
      ? phoneCountry.toLowerCase()
      : undefined;
    const isBookingGuestFlow = this.isBookingGuestFlow();

    return (
      <Form
        onSubmit={(event) => {
          event.preventDefault();
          this.handleProfileSubmit();
        }}>
        {isBookingGuestFlow ? (
          <div className='auth-role-selector'>
            <button
              type='button'
              className='auth-role-button is-active'
              disabled>
              {t('auth.roleGuest', { defaultValue: 'Guest' })}
            </button>
          </div>
        ) : (
          <div className='auth-role-selector'>
            {ROLE_OPTIONS.map((role) => (
              <button
                type='button'
                key={role}
                className={`auth-role-button${profile.role === role ? ' is-active' : ''}`}
                onClick={() => this.handleRoleChange(role)}>
                {t(`auth.role${role}`)}
              </button>
            ))}
          </div>
        )}
        <Row>
          <Col xs={12} md={6}>
            <FormInput
              id='auth_first_name'
              label={t('auth.firstName')}
              value={profile.firstName}
              error={profileErrors.firstName}
              autoFocus={!isMobileMode()}
              inputRef={this.setFirstNameInputRef}
              onChange={(event) => {
                const value = event.target.value;
                this.setState((prevState) => ({
                  profile: {
                    ...prevState.profile,
                    firstName: value,
                  },
                }));
              }}
            />
          </Col>
          <Col xs={12} md={6}>
            <FormInput
              id='auth_last_name'
              label={t('auth.lastName')}
              value={profile.lastName}
              error={profileErrors.lastName}
              onChange={(event) => {
                const value = event.target.value;
                this.setState((prevState) => ({
                  profile: {
                    ...prevState.profile,
                    lastName: value,
                  },
                  profileErrors: {
                    ...prevState.profileErrors,
                    lastName: undefined,
                  },
                }));
              }}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={6}>
            <FormInput
              id='auth_language'
              label={t('auth.communicationLanguage')}
              as='select'
              value={profile.language}
              onChange={(event) => {
                const value = event.target.value;
                this.setState((prevState) => ({
                  profile: {
                    ...prevState.profile,
                    language: value,
                  },
                }));
              }}>
              {COMMUNICATION_LANGUAGES.map((language) => (
                <option key={language} value={language}>
                  {language}
                </option>
              ))}
            </FormInput>
          </Col>
          <Col xs={12} md={6}>
            <AuthPhoneInput
              id='auth_phone'
              label={t('auth.mobileNumber')}
              value={profile.phone}
              error={profileErrors.phone}
              defaultCountry={defaultPhoneCountry}
              onCountryChange={this.handlePhoneCountryChange}
              onChange={(event) => {
                const value = event.target.value;
                this.setState((prevState) => ({
                  profile: {
                    ...prevState.profile,
                    phone: value,
                  },
                  profileErrors: {
                    ...prevState.profileErrors,
                    phone: undefined,
                  },
                }));
              }}
            />
          </Col>
        </Row>
        <FormCheckbox
          id='auth_accept_terms'
          label={t('auth.acceptTerms')}
          checked={profile.acceptTerms}
          error={profileErrors.acceptTerms}
          onChange={() => {
            this.setState((prevState) => ({
              profile: {
                ...prevState.profile,
                acceptTerms: !prevState.profile.acceptTerms,
              },
              profileErrors: {
                ...prevState.profileErrors,
                acceptTerms: undefined,
              },
            }));
          }}
          last
        />
        <div className='auth-actions'>
          <div>
            <Button
              type='button'
              variant='link'
              onClick={this.handleBack}
              disabled={loading}>
              {t('auth.back')}
            </Button>
            <Button type='submit' disabled={loading}>
              {t('auth.next')}
            </Button>
          </div>
        </div>
      </Form>
    );
  };

  renderMobileStep = () => {
    const { t } = this.props;
    const {
      profile,
      profileErrors,
      mobileCode,
      codeErrors,
      loading,
      smsCountdown,
      phoneCountry,
    } = this.state;
    const defaultPhoneCountry = phoneCountry
      ? phoneCountry.toLowerCase()
      : undefined;

    return (
      <Form
        onSubmit={(event) => {
          event.preventDefault();
          this.handleVerifyMobile();
        }}>
        <AuthPhoneInput
          id='auth_mobile_number_confirm'
          label={t('auth.mobileNumber')}
          value={profile.phone}
          error={profileErrors.phone}
          defaultCountry={defaultPhoneCountry}
          onCountryChange={this.handlePhoneCountryChange}
          onChange={(event) => {
            const value = event.target.value;
            this.setState((prevState) => ({
              profile: {
                ...prevState.profile,
                phone: value,
              },
              profileErrors: {
                ...prevState.profileErrors,
                phone: undefined,
              },
            }));
          }}
        />
        <VerificationCodeInput
          value={mobileCode}
          onChange={(value) => {
            this.setState((prevState) => ({
              mobileCode: value,
              codeErrors: {
                ...prevState.codeErrors,
                mobile: undefined,
              },
            }));
          }}
          idPrefix='auth_mobile_code'
          length={5}
          label={t('auth.verificationCodeLabel', {
            activationCodeLabel: t('auth.activationCodeLabel'),
          })}
        />
        {codeErrors.mobile && (
          <div className='auth-error-inline'>{codeErrors.mobile}</div>
        )}
        <div className='auth-inline-actions'>
          <Button
            type='button'
            variant='link'
            className='auth-resend-link'
            onClick={this.handleSendSms}
            disabled={loading || smsCountdown > 0}>
            {smsCountdown > 0
              ? `${t('auth.resendSmsCountdown')} ${smsCountdown}s`
              : t('auth.resendSms')}
          </Button>
        </div>
        <div className='auth-actions'>
          <div>
            <Button
              type='button'
              variant='link'
              onClick={this.handleBack}
              disabled={loading}>
              {t('auth.back')}
            </Button>
            <Button type='submit' disabled={loading}>
              {t('auth.next')}
            </Button>
          </div>
        </div>
      </Form>
    );
  };

  renderCompletionStep = () => {
    const { t } = this.props;
    const {
      emailConfirmed,
      mobileConfirmed,
      boatAdded,
      paymentAdded,
      loading,
    } = this.state;
    const isFullyComplete =
      emailConfirmed && mobileConfirmed && boatAdded && paymentAdded;

    return (
      <div className='auth-completion'>
        <div className='auth-completion-status'>
          <div
            className={`auth-status-item${emailConfirmed ? ' is-complete' : ''}`}>
            <span className='auth-status-indicator' />
            {t('auth.statusEmail')}
            {!emailConfirmed && (
              <Button
                variant='link'
                className='py-0'
                onClick={() => {
                  this.setState({
                    codeErrors: {},
                    emailCode: '',
                    currentStep: STEP_EMAIL_CONFIRM,
                  });
                }}>
                {t('auth.goToStep')}
              </Button>
            )}
          </div>
          <div
            className={`auth-status-item${mobileConfirmed ? ' is-complete' : ''}`}>
            <span className='auth-status-indicator' />
            {t('auth.statusMobile')}
            {!mobileConfirmed && (
              <Button
                variant='link'
                className='py-0'
                onClick={() => {
                  this.setState({
                    hasTriggeredSms: false,
                    smsCountdown: 0,
                    codeErrors: {},
                    mobileCode: '',
                    currentStep: STEP_MOBILE_CONFIRM,
                  });
                }}>
                {t('auth.goToStep')}
              </Button>
            )}
          </div>
          <div className={`auth-status-item${boatAdded ? ' is-complete' : ''}`}>
            <span className='auth-status-indicator' />
            {t('auth.statusBoat')}
            {!boatAdded && (
              <Button
                variant='link'
                className='py-0'
                onClick={() => {
                  this.setState({
                    boatErrors: {},
                    currentStep: STEP_BOAT,
                    returnToCompletionAfterBoat: true,
                  });
                }}>
                {t('auth.goToStep')}
              </Button>
            )}
          </div>
          <div
            className={`auth-status-item${paymentAdded ? ' is-complete' : ''}`}>
            <span className='auth-status-indicator' />
            {t('auth.statusPayment')}
            {!paymentAdded && (
              <Button
                variant='link'
                className='py-0'
                onClick={() => {
                  this.setState({
                    paymentErrors: {},
                    currentStep: STEP_PAYMENT,
                    returnToCompletionAfterBoat: false,
                  });
                }}>
                {t('auth.goToStep')}
              </Button>
            )}
          </div>
        </div>
        <div className='auth-actions auth-actions--completion'>
          {isFullyComplete ? (
            <Button
              onClick={() => {
                this.handleLoginSubmit();
              }}
              disabled={loading}>
              {t('auth.next')}
            </Button>
          ) : (
            <Button
              variant='link'
              className='auth-skip-link'
              onClick={() => {
                this.handleLoginSubmit();
              }}
              disabled={loading}>
              {t('auth.loginNow')}
            </Button>
          )}
        </div>
      </div>
    );
  };

  renderBoatStep = () => {
    const { t } = this.props;
    const { boatDetails, boatErrors, loading } = this.state;
    const boatTypes = this.getBoatTypes();
    const allowSkipBoat = !this.isBookingGuestFlow();

    return (
      <Form
        onSubmit={(event) => {
          event.preventDefault();
          this.handleBoatSubmit();
        }}>
        <Row>
          <Col xs={12}>
            <FormInput
              id='auth_boat_type'
              label={t('auth.boatType')}
              as='select'
              value={boatDetails.type != null ? String(boatDetails.type) : ''}
              error={boatErrors.type}
              onChange={(event) => {
                const value =
                  event.target.value === '' ? null : Number(event.target.value);
                this.setState((prevState) => ({
                  boatDetails: {
                    ...prevState.boatDetails,
                    type: value,
                  },
                }));
              }}>
              <option value=''>{t('auth.selectBoatType')}</option>
              {boatTypes.map((type, index) => (
                <option key={`${type}-${index}`} value={index}>
                  {type}
                </option>
              ))}
            </FormInput>
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <FormInput
              id='auth_boat_name'
              label={t('auth.boatName')}
              value={boatDetails.name}
              error={boatErrors.name}
              onChange={(event) => {
                const value = event.target.value;
                this.setState((prevState) => ({
                  boatDetails: {
                    ...prevState.boatDetails,
                    name: value,
                  },
                }));
              }}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <FormInput
              id='auth_boat_registration'
              label={t('auth.boatRegistration')}
              value={boatDetails.registration}
              error={boatErrors.registration}
              onChange={(event) => {
                const value = event.target.value;
                this.setState((prevState) => ({
                  boatDetails: {
                    ...prevState.boatDetails,
                    registration: value,
                  },
                }));
              }}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={4}>
            <FormInput
              id='auth_boat_length'
              label={t('auth.boatLength')}
              value={boatDetails.length}
              error={boatErrors.length}
              onChange={(event) => {
                const value = event.target.value;
                this.setState((prevState) => ({
                  boatDetails: {
                    ...prevState.boatDetails,
                    length: value,
                  },
                }));
              }}
            />
          </Col>
          <Col xs={12} md={4}>
            <FormInput
              id='auth_boat_beam'
              label={t('auth.boatBeam')}
              value={boatDetails.beam}
              error={boatErrors.beam}
              onChange={(event) => {
                const value = event.target.value;
                this.setState((prevState) => ({
                  boatDetails: {
                    ...prevState.boatDetails,
                    beam: value,
                  },
                }));
              }}
            />
          </Col>
          {boatDetails.type !== CAMPER_WOMO_INDEX && (
            <Col xs={12} md={4}>
              <FormInput
                id='auth_boat_draft'
                label={t('auth.boatDraft')}
                value={boatDetails.draft}
                error={boatErrors.draft}
                onChange={(event) => {
                  const value = event.target.value;
                  this.setState((prevState) => ({
                    boatDetails: {
                      ...prevState.boatDetails,
                      draft: value,
                    },
                  }));
                }}
              />
            </Col>
          )}
        </Row>
        <div className='auth-actions'>
          <div>
            <Button
              type='button'
              variant='link'
              onClick={this.handleBack}
              disabled={loading}>
              {t('auth.back')}
            </Button>
            <Button type='submit' disabled={loading}>
              {t('auth.next')}
            </Button>
          </div>
          {allowSkipBoat && (
            <Button
              type='button'
              variant='link'
              className='auth-skip-link'
              onClick={this.handleBoatSkip}
              disabled={loading}>
              {t('auth.skipForNow')}
            </Button>
          )}
        </div>
      </Form>
    );
  };

  renderPaymentStep = () => {
    const { t } = this.props;
    const { paymentDetails, paymentErrors, loading } = this.state;

    return (
      <Form
        onSubmit={(event) => {
          event.preventDefault();
          this.handlePaymentSubmit();
        }}>
        {/* <div className='auth-payment-instructions'>
          {t('auth.paymentInstructions')}
        </div> */}
        <FormInput
          id='auth_payment_currency'
          label={t('auth.paymentCurrency')}
          as='select'
          value={paymentDetails.currency}
          error={paymentErrors.currency}
          onChange={(event) => {
            const value = event.target.value;
            this.setState((prevState) => ({
              paymentDetails: {
                ...prevState.paymentDetails,
                currency: value,
              },
              paymentErrors: {
                ...prevState.paymentErrors,
                currency: undefined,
              },
            }));
          }}>
          <option value=''></option>
          {CURRENCIES.map((currency) => (
            <option key={currency} value={currency}>
              {currency}
            </option>
          ))}
        </FormInput>
        <div className='auth-actions'>
          <div>
            <Button variant='link' onClick={this.handleBack} disabled={loading}>
              {t('auth.back')}
            </Button>
            <Button type='submit' disabled={loading}>
              {t('auth.next')}
            </Button>
          </div>
          <Button
            type='button'
            variant='link'
            className='auth-skip-link'
            onClick={this.handlePaymentSkip}
            disabled={loading}>
            {t('auth.skipForNow')}
          </Button>
        </div>
      </Form>
    );
  };

  renderStep = () => {
    const { currentStep } = this.state;

    if (currentStep === STEP_EMAIL) {
      return this.renderEmailStep();
    }
    if (currentStep === STEP_LOGIN) {
      return this.renderLoginStep();
    }
    if (currentStep === STEP_REGISTER_CHOICE) {
      return this.renderRegisterChoiceStep();
    }
    if (currentStep === STEP_REGISTER_PASSWORD) {
      return this.renderRegisterStep();
    }
    if (currentStep === STEP_EMAIL_CONFIRM) {
      return this.renderEmailConfirmStep();
    }
    if (currentStep === STEP_PROFILE) {
      return this.renderProfileStep();
    }
    if (currentStep === STEP_MOBILE_CONFIRM) {
      return this.renderMobileStep();
    }
    if (currentStep === STEP_BOAT) {
      return this.renderBoatStep();
    }
    if (currentStep === STEP_PAYMENT) {
      return this.renderPaymentStep();
    }
    return this.renderCompletionStep();
  };

  render() {
    const { t } = this.props;
    const { errorMessage, successMessage, uiLanguage, stepLoading } =
      this.state;

    const stepTitle = this.getStepTitle();
    const hintText = this.getHintText();
    const renderCharacterGuide = () => <CharacterGuide hint={hintText} />;
    const mobileMode = isMobileMode();

    return (
      <>
        <div
          className={`auth-wizard${mobileMode ? ' auth-wizard--mobile' : ''}`}>
          <div className='auth-wizard__content'>
            <div className='auth-header'>
              <div className='auth-header__top'>
                <img src={logoSvg} alt='BoatPark' className='auth-logo' />
                <div className='auth-language-select-wrapper'>
                  <span
                    className='auth-language-select__icon'
                    aria-hidden='true'>
                    <svg
                      width='16'
                      height='16'
                      viewBox='0 0 24 24'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                      aria-hidden='true'
                      focusable='false'>
                      <circle
                        cx='12'
                        cy='12'
                        r='9'
                        stroke='currentColor'
                        strokeWidth='1.5'
                        fill='none'
                      />
                      <ellipse
                        cx='12'
                        cy='12'
                        rx='4.5'
                        ry='9'
                        stroke='currentColor'
                        strokeWidth='1.5'
                        fill='none'
                      />
                      <path
                        d='M3 12h18M4.5 7.5h15M4.5 16.5h15'
                        stroke='currentColor'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                      />
                    </svg>
                  </span>
                  <select
                    className='auth-language-select'
                    value={uiLanguage}
                    onChange={this.handleUiLanguageChange}>
                    {COMMUNICATION_LANGUAGES.map((language) => (
                      <option key={language} value={language}>
                        {language}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <h1>{t('auth.title')}</h1>
              <div className='auth-mobile-guide'>{renderCharacterGuide()}</div>
              <div className='auth-step-title'>{stepTitle}</div>
            </div>
            {errorMessage && <Alert variant='danger'>{errorMessage}</Alert>}
            {successMessage && (
              <Alert variant='success'>{successMessage}</Alert>
            )}
            <div
              className={`auth-step-wrapper${stepLoading ? ' is-loading' : ''}`}>
              {stepLoading && (
                <div
                  className='auth-step-loading'
                  aria-live='polite'
                  aria-busy='true'>
                  <div className='auth-step-spinner' />
                </div>
              )}
              <div className='auth-step-body'>{this.renderStep()}</div>
            </div>
          </div>
          <div className='auth-desktop-guide'>{renderCharacterGuide()}</div>
        </div>
      </>
    );
  }
}

// Wrapper component to inject reCaptcha hook into Auth class
function AuthWithReCaptcha(props) {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const authRef = useRef(null);

  useEffect(() => {
    if (authRef.current && executeRecaptcha) {
      authRef.current.setExecuteRecaptcha(executeRecaptcha);
    }
  }, [executeRecaptcha]);

  return <Auth {...props} ref={authRef} />;
}

export default withTranslation()(withRouter(AuthWithReCaptcha));
